import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, CircularProgress, Box, Typography, Grid, Paper, Rating, Accordion, AccordionSummary, AccordionDetails, TextField, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from '@tanstack/react-query';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import InfoIcon from '@mui/icons-material/Info';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useLocation } from "react-router-dom";
import useWindowDimensions from '../../../Reusables/VisualAndAccord/Hooks/useWindowDimensions';
import PartnersSection from '../Home/components/PartnersSection';
import PlanQuickFacts from '../Employer401k/components/PlanQuickFacts';
import PlanAnalysis from '../Employer401k/components/PlanAnalysis';
import Canonical from '../../../Utils/Canonical/Canonical';
import PageHeaders from '../../../Utils/PageHeaders';
import MyTooltip from '../../../Utils/Tooltip/Tooltip';
import HubNav from '../../ResourceHubPages/HubNav/HubNav';

import { fetchEmployer403bData } from './api';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#FFFFFF',
    paddingBottom: theme.spacing(0),
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
  },
  // New Hero Styles
  hero: {
    padding: theme.spacing(6, 0, 5),
    background: 'linear-gradient(135deg, #03060d 0, #060708 100%);',
    position: 'relative',
    overflow: 'hidden',
    color: '#FFFFFF',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'radial-gradient(ellipse 70% 80% at 90% 50%, rgba(0,181,164,0.2) 0%, transparent 65%)',
      pointerEvents: 'none',
    },
  },
  heroTitle: {
    fontSize: 'clamp(26px, 4vw, 38px)',
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: '-0.5px',
    marginBottom: theme.spacing(2),
  },
  infoIcon: {
    fontSize: '1rem',
    color: 'rgba(255, 255, 255, 0.6)',
    cursor: 'pointer',
    '&:hover': {
      color: '#00b5a4',
    },
  },
  // Optimization Section
  section: {
    padding: theme.spacing(8, 0),
    borderTop: '1px solid #e2e8f0',
  },
  secLabel: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: '#00b5a4',
    marginBottom: theme.spacing(1),
  },
  secTitle: {
    fontSize: 'clamp(22px, 3vw, 30px)',
    fontWeight: 800,
    color: '#0d1f3c',
    letterSpacing: '-0.3px',
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.2,
  },
  secSub: {
    fontSize: '15px',
    color: '#334155',
    maxWidth: '640px',
    lineHeight: 1.65,
    marginBottom: theme.spacing(4),
  },
  optCard: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderTop: '3px solid #00b5a4',
    borderRadius: '12px',
    padding: theme.spacing(2.5, 3),
    height: '100%',
    boxShadow: '0 1px 4px rgba(13,31,60,0.08)',
  },
  optIcon: {
    fontSize: '22px',
    marginBottom: theme.spacing(1.5),
  },
  optTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#0d1f3c',
    marginBottom: theme.spacing(1),
  },
  optDesc: {
    fontSize: '13px',
    color: '#334155',
    lineHeight: 1.6,
  },
  optAssumption: {
    fontSize: '11px',
    color: '#64748b',
    marginTop: theme.spacing(1),
    fontStyle: 'italic',
  },
  // FAQ Styles
  faqAccordion: {
    boxShadow: '0 1px 4px rgba(13,31,60,0.08)',
    border: '1px solid #e2e8f0',
    borderRadius: '12px !important',
    marginBottom: theme.spacing(1.5),
    '&:before': {
      display: 'none',
    },
  },
  faqSummary: {
    padding: theme.spacing(1, 2.5),
    '& .MuiTypography-root': {
      fontSize: '14px',
      fontWeight: 600,
      color: '#0d1f3c',
    },
  },
  faqDetails: {
    padding: theme.spacing(0, 2.5, 2),
    fontSize: '14px',
    color: '#334155',
    lineHeight: 1.7,
  },
  // Social Proof
  socialProof: {
    background: 'linear-gradient(135deg, #0d1f3c 0%, #0d3348 50%, #005950 100%)',
    padding: theme.spacing(7, 0),
    textAlign: 'center',
    color: '#FFFFFF',
  },
  socialStat: {
    fontSize: 'clamp(16px, 2.5vw, 21px)',
    color: 'rgba(255,255,255,0.75)',
    maxWidth: '600px',
    margin: '0 auto 14px',
    lineHeight: 1.55,
    '& strong': {
      color: '#00b5a4',
      fontWeight: 800,
    },
  },
  socialSub: {
    fontSize: '15px',
    color: 'rgba(255,255,255,0.85)',
    fontWeight: 500,
    maxWidth: '480px',
    margin: '0 auto 22px',
  },
  appBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#00b5a4',
    color: 'white',
    borderRadius: '12px',
    padding: '12px 24px',
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: 700,
    transition: 'background .2s, transform .15s',
    boxShadow: '0 4px 16px rgba(0,181,164,0.3)',
    '&:hover': {
      backgroundColor: '#009688',
      transform: 'translateY(-2px)',
      color: 'white',
    },
  },
  // Subscribe
  subscribeSec: {
    padding: theme.spacing(5, 0),
    borderTop: '1px solid #e2e8f0',
    textAlign: 'center',
  },
  subTitle: {
    fontSize: '19px',
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '.5px',
    marginBottom: theme.spacing(1),
  },
  subText: {
    fontSize: '15px',
    color: '#00b5a4',
    fontWeight: 500,
    maxWidth: '480px',
    margin: '0 auto 2.5rem',
    lineHeight: 1.6,
  },
  subForm: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    flexWrap: 'wrap',
    maxWidth: '420px',
    margin: '0 auto',
  },
  subInput: {
    flex: 1,
    minWidth: '200px',
  },
  subBtn: {
    backgroundColor: '#00b5a4',
    color: 'white',
    fontWeight: 700,
    padding: '10px 24px',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#009688',
    },
  },
}));

const FACTS = [
  {
    key: 'fundOptions',
    icon: TrackChangesIcon,
    label: 'Fund Options',
    color: '#4361EE',
    tooltipText: 'Reflects the number of investment options available in the plan. A moderate number of funds receives the highest rating, as very few or excessive options may hinder effective diversification or decision‑making.',
    gridMd: 6,
  },
  {
    key: 'expenseRatio',
    icon: TrendingDownIcon,
    label: 'Average Expense Ratio',
    color: '#10B981',
    tooltipText: 'Provides a reliable indicator of plan cost levels, based on recently available information. It reflects a weighted average of core investment options.',
    gridMd: 6,
  },
];

const OPTIMIZATION_DATA = [
  {
    icon: '💸',
    title: 'Hidden Fees Compound Over Decades',
    desc: 'Even a 0.5% difference in expense ratios can cost tens of thousands of dollars over a long career. Plootus identifies low-cost alternatives within your plan\'s lineup to keep more of your money working for you.',
    assumption: 'Assumes $100,000 starting balance, 7% annual return, and a 30-year investment horizon. Actual results will vary.'
  },
  {
    icon: '📊',
    title: 'Default Funds May Underperform',
    desc: 'Many employees remain in auto-enrolled default funds without reviewing whether they\'re the best option. A more tailored allocation — matched to your age and risk tolerance — may deliver better long-term outcomes.'
  },
  {
    icon: '📅',
    title: 'Catch-Up Contributions Matter After 50',
    desc: 'In 2026, employees aged 50+ can contribute an extra $8,000 beyond the $24,500 standard limit (total: $32,500). Employees aged 60–63 may contribute up to $11,250 extra under the SECURE 2.0 Act (total: $35,750) — a critical accelerator in the final years before retirement.'
  },
  {
    icon: '🤖',
    title: 'AI Makes Optimization Effortless',
    desc: 'Plootus analyzes your plan\'s complete fund lineup — performance, fees, and risk — and recommends a personalized allocation strategy in minutes. No financial jargon, no advisor fees, and no Social Security number required.'
  }
];

const FAQ_DATA = [
  {
    q: "What is the IRS contribution limit for 2026?",
    a: "For 2026, the IRS elective deferral limit for 401(k), 403(b), and most 457 plans is $24,500 (up from $23,500 in 2025). Employees age 50 or older may contribute an additional $8,000 catch-up contribution, bringing the total to $32,500."
  },
  {
    q: "What is the enhanced catch-up limit for employees aged 60–63?",
    a: "Under the SECURE 2.0 Act, employees aged 60, 61, 62, or 63 may make an enhanced \"super\" catch-up contribution of $11,250 in 2026 — rather than the standard $8,000 — for a total possible deferral of $35,750. Note: beginning in 2026, employees who earned more than $150,000 in prior-year FICA wages must make all catch-up contributions as Roth (after-tax) contributions."
  },
  {
    q: "What is the new Roth catch-up rule for high earners in 2026?",
    a: "Starting January 1, 2026, employees who earned more than $150,000 in FICA (Social Security) wages in the prior year must make all age-based catch-up contributions as Roth (after-tax) contributions — they can no longer be made pre-tax. If your employer's plan does not offer a Roth option, high earners may lose the ability to make catch-up contributions entirely."
  },
  {
    q: "Are my retirement plan contributions tax-deductible?",
    a: "Traditional pre-tax contributions reduce your taxable income in the year of contribution, lowering your current tax bill. Roth contributions are made with after-tax dollars and grow tax-free, with qualified withdrawals also tax-free in retirement. Always consult a qualified tax professional for advice tailored to your situation."
  },
  {
    q: "What is an expense ratio and why does it matter?",
    a: "An expense ratio is the annual fee a mutual fund charges, expressed as a percentage of your invested assets. These fees are deducted automatically and are easy to overlook — but they compound significantly over decades. For example, reducing fees by 0.5% on a $100,000 portfolio could save over $70,000 over 30 years, assuming a 7% annual return."
  },
  {
    q: "What is a target-date fund and should I use one?",
    a: "A target-date fund automatically shifts its allocation from growth-oriented (more equities) to conservative (more bonds) as you approach your expected retirement year. They are a convenient default option, but not always the most cost-effective choice. Plootus analyzes whether a target-date fund or a custom combination of individual plan funds may serve you better."
  },
  {
    q: "How does Plootus work and is it really free?",
    a: "Yes — Plootus is free to use. Search for your employer plan, select a risk strategy, and Plootus suggests an optimized fund allocation based on performance and expense ratios. No Social Security number required. Plootus is an SEC-registered investment adviser that generates revenue through partnerships, not by charging users for plan analysis."
  }
];

const Employer403b = () => {
  const classes = useStyles();
  const { employerName } = useParams();
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const [strategy, setStrategy] = useState(2);
  const location = useLocation();

  const {
    data: employerData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['employer403b', employerName],
    queryFn: () => fetchEmployer403bData(employerName),
    enabled: !!employerName,
    onError: (err) => {
      console.error('Error fetching employer 403b data:', err);
      navigate('/404');
    },
    staleTime: 1000 * 60 * 10,
    cacheTime: 1000 * 60 * 60,
  });

  if (isLoading) {
    return (
      <Box className={classes.loaderContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !employerData) {
    return null;
  }

  return (
    <>
    <Canonical />
    <PageHeaders
      title={`${employerData.employer_name} 403(b) Plan Details | Plootus`}
      description={`Explore ${employerData.employer_name}'s 403(b) plan, including fees, fund options, and performance insights.`}
      canonicalUrl={`https://www.plootus.com/${employerName}`}
    />
    <HubNav />
    <div className={classes.root}>
      {/* Hero Banner */}
      <div className={classes.hero}>
        <Container maxWidth="lg">
          <Typography variant="h1" className={classes.heroTitle}>
            {employerData.employer_name}
          </Typography>
          {employerData.plan_rating !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Rating
                value={employerData.plan_rating}
                readOnly
                size="large"
                emptyIcon={<StarIcon style={{ opacity: 0.55, color: '#d1d5db' }} fontSize="inherit" />}
                sx={{ '& .MuiRating-iconFilled': { color: '#f59e0b' } }}
              />
              <MyTooltip
                title="The combined plan rating is the average of the number-of-funds rating and the average expense ratio rating."
                render={() => <InfoIcon className={classes.infoIcon} />}
              />
            </Box>
          )}
        </Container>
      </div>

      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <PlanQuickFacts
          title={null}
          facts={FACTS.map((fact) => ({
            ...fact,
            data:
              fact.key === 'fundOptions'
                ? {
                    subHeader: employerData.fund_options_sub_header,
                    description: employerData.fund_options_description,
                    rating: employerData.fund_options_star_rating,
                  }
                : {
                    subHeader: employerData.average_expense_ratio_sub_header,
                    description:
                      employerData.average_expense_ratio_description,
                    rating: employerData.average_expense_ratio_star_rating,
                  },
          }))}
        />

        <PlanAnalysis
          planType="403(b)"
          strategy={strategy}
          setStrategy={setStrategy}
          width={width}
          funds={employerData.allocations}
          fees={employerData.fees}
          createdAt={employerData.createdAt}
        />
      </Container>

      <PartnersSection />

      {/* Retirement Intelligence */}
      <div className={classes.section}>
        <Container maxWidth="lg">
          <Typography className={classes.secLabel}>Retirement Intelligence</Typography>
          <Typography variant="h2" className={classes.secTitle}>Why Optimizing Your Plan Matters</Typography>
          <Typography className={classes.secSub}>Small adjustments to your retirement fund allocation can have an outsized impact over your career. Here's what every plan participant should keep in mind.</Typography>
          <Grid container spacing={2}>
            {OPTIMIZATION_DATA.map((item, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <div className={classes.optCard}>
                  <div className={classes.optIcon}>{item.icon}</div>
                  <Typography variant="h3" className={classes.optTitle}>{item.title}</Typography>
                  <Typography className={classes.optDesc}>{item.desc}</Typography>
                  {item.assumption && <Typography className={classes.optAssumption}>{item.assumption}</Typography>}
                </div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </div>

      {/* FAQ Section */}
      <div className={classes.section} style={{ backgroundColor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Typography className={classes.secLabel}>Common Questions</Typography>
          <Typography variant="h2" className={classes.secTitle}>Retirement Plan FAQs</Typography>
          <Typography className={classes.secSub}>General guidance on IRS contribution limits, tax treatment, and how Plootus helps you make the most of your employer plan.</Typography>
          <Box sx={{ maxWidth: '800px' }}>
            {FAQ_DATA.map((item, idx) => (
              <Accordion key={idx} className={classes.faqAccordion} elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#00b5a4' }} />} className={classes.faqSummary}>
                  <Typography>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.faqDetails}>
                  <Typography variant="body2">{item.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </div>

    </div>
    </>
  );
};

export default Employer403b;
